import React, { useState } from 'react';
import {
    ClockCounterClockwiseIcon,
    TrashIcon,
    XIcon,
} from '@phosphor-icons/react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Button } from '@/components/ui/Button';

interface RecentQueriesDropdownProps {
    queries: string[];
    onSelect: (query: string) => void;
    onRemove: (query: string) => void;
    onClearAll: () => void;
    label: string;
    emptyLabel: string;
    removeLabel: string;
    clearLabel: string;
    triggerClassName?: string;
}

export function RecentQueriesDropdown({
    queries,
    onSelect,
    onRemove,
    onClearAll,
    label,
    emptyLabel,
    removeLabel,
    clearLabel,
    triggerClassName,
}: RecentQueriesDropdownProps) {
    const [open, setOpen] = useState(false);

    const handleSelect = (query: string) => {
        onSelect(query);
        setOpen(false);
    };

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    shape="square"
                    className={triggerClassName}
                    leadingIcon={
                        <ClockCounterClockwiseIcon className="size-4" />
                    }
                    aria-label={label}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                side="top"
                align="start"
                sideOffset={8}
                className="w-[300px]"
            >
                <div className="flex w-full items-center justify-between gap-2 pr-2">
                    <DropdownMenuLabel className="min-w-0 flex-1 truncate">
                        {label}
                    </DropdownMenuLabel>
                    {queries.length > 0 && (
                        <button
                            type="button"
                            onClick={onClearAll}
                            className="flex shrink-0 items-center gap-1 text-[12px] leading-4 text-grey-300 transition-colors hover:text-white-100"
                        >
                            <TrashIcon className="size-3" />
                            {clearLabel}
                        </button>
                    )}
                </div>
                <DropdownMenuGroup className="max-h-[212px] w-full overflow-y-auto">
                    {queries.length === 0 ? (
                        <div className="px-2 py-6 text-center text-[14px] leading-5 text-grey-200">
                            {emptyLabel}
                        </div>
                    ) : (
                        queries.map((query) => (
                            <DropdownMenuItemBase
                                key={query}
                                className="group/item"
                                onSelect={() => handleSelect(query)}
                            >
                                <span className="min-w-0 flex-1 truncate">
                                    {query}
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemove(query);
                                    }}
                                    className="shrink-0 text-grey-300 opacity-0 transition-opacity hover:text-white-100 group-hover/item:opacity-100"
                                    aria-label={removeLabel}
                                >
                                    <XIcon className="size-3.5" />
                                </button>
                            </DropdownMenuItemBase>
                        ))
                    )}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
